import type {
  AuthorizationContext,
  BuildAuthorizationContextInput,
} from "./types/authorization"
import {
  type Permission,
  VALID_PERMISSIONS,
  expandPermissionDependencies,
  isTenantOnlyPermission,
} from "./types/permissions"

const tenantPermissions = new Set<Permission>(VALID_PERMISSIONS)

export const isAuthorizationDocumentId = (value: unknown): value is string =>
  typeof value === "string" &&
  value.length > 0 &&
  value.trim() === value &&
  !value.includes("/") &&
  value !== "." &&
  value !== ".."

export const isRoleScopePolicy = (
  value: unknown,
): value is "tenant" | "assigned-teams" =>
  value === "tenant" || value === "assigned-teams"

export const isTenantPermission = (value: unknown): value is Permission =>
  typeof value === "string" &&
  value !== "*" &&
  tenantPermissions.has(value as Permission)

export const normalizeTenantRolePermissions = (
  value: unknown,
  scopePolicy: unknown,
): Permission[] | null => {
  if (
    !isRoleScopePolicy(scopePolicy) ||
    !Array.isArray(value) ||
    !value.every(isTenantPermission) ||
    (scopePolicy === "assigned-teams" && value.some(isTenantOnlyPermission))
  ) {
    return null
  }

  return [...new Set(value)]
}

export const normalizeManagedTeamIds = (value: unknown): string[] | null => {
  if (!Array.isArray(value)) return null

  const normalized = new Set<string>()
  for (const teamId of value) {
    if (!isAuthorizationDocumentId(teamId)) return null
    normalized.add(teamId)
  }

  return [...normalized]
}

/** Resolve persisted target membership only, never infer managed-team authority. */
export const resolveAuthorizationTeamReferences = (
  references: unknown,
  teams: readonly { id: string; name?: unknown }[],
): string[] | null => {
  if (!Array.isArray(references)) return null
  const aliases = new Map<string, Set<string>>()
  for (const team of teams) {
    if (!isAuthorizationDocumentId(team.id)) return null
    for (const alias of new Set([team.id, ...(typeof team.name === "string" && team.name ? [team.name] : [])])) {
      const ids = aliases.get(alias) ?? new Set<string>()
      ids.add(team.id)
      aliases.set(alias, ids)
    }
  }
  const resolved = new Set<string>()
  for (const reference of references) {
    if (typeof reference !== "string") return null
    const matches = aliases.get(reference)
    if (!matches || matches.size !== 1) return null
    for (const id of matches) resolved.add(id)
  }
  return [...resolved]
}

export const buildAuthorizationContext = (
  input: BuildAuthorizationContextInput,
): AuthorizationContext | null => {
  const { tenantId, actorUserId, role, assignment } = input
  const rolePermissions = normalizeTenantRolePermissions(
    role?.permissions,
    role?.scopePolicy,
  )
  if (
    !isAuthorizationDocumentId(tenantId) ||
    !isAuthorizationDocumentId(actorUserId) ||
    !isAuthorizationDocumentId(role?.id) ||
    rolePermissions === null ||
    assignment?.userId !== actorUserId ||
    assignment.roleId !== role.id
  ) {
    return null
  }

  const managedTeamIds = normalizeManagedTeamIds(assignment.managedTeamIds)
  if (
    managedTeamIds === null ||
    (role.scopePolicy === "assigned-teams" && managedTeamIds.length === 0) ||
    (role.scopePolicy === "tenant" && managedTeamIds.length !== 0)
  ) {
    return null
  }

  const permissions = expandPermissionDependencies(rolePermissions)
  const normalizedAssignment = { ...assignment, managedTeamIds }

  return {
    tenantId,
    actorUserId,
    role: { ...role, permissions },
    assignment: normalizedAssignment,
    permissions,
    scopePolicy: role.scopePolicy,
    managedTeamIds,
  }
}

export const authorizationHasPermission = (
  context: AuthorizationContext | null | undefined,
  permission: Permission,
): boolean =>
  context !== null &&
  context !== undefined &&
  permission !== "*" &&
  context.permissions.includes(permission)

export const isTargetInScope = (
  context: AuthorizationContext | null | undefined,
  targetTeamIds: unknown,
): boolean => {
  if (!context) return false
  const normalizedTargetIds = normalizeManagedTeamIds(targetTeamIds)
  if (!normalizedTargetIds) return false
  if (context.scopePolicy === "tenant") return true
  if (normalizedTargetIds.length === 0) return false

  const managedTeamIds = new Set(context.managedTeamIds)
  return normalizedTargetIds.some((teamId) => managedTeamIds.has(teamId))
}

type UserScopeTarget = { teams?: unknown; teamIds?: unknown }
type PersonScopeTarget = { teamIds?: unknown }
type TeamScopeTarget = { id?: unknown }
type ActivityScopeTarget = { assignedTeams?: unknown }
type HoursScopeTarget = { assignedTeams?: unknown }

const isScopeTarget = (target: unknown): target is Record<string, unknown> =>
  target !== null && typeof target === "object" && !Array.isArray(target)

export const isUserInScope = (
  context: AuthorizationContext | null | undefined,
  target: UserScopeTarget | null | undefined,
): boolean => {
  if (!isScopeTarget(target)) return false
  const teams = normalizeManagedTeamIds(target.teams ?? [])
  const teamIds = normalizeManagedTeamIds(target.teamIds ?? [])
  if (!teams || !teamIds) return false
  return isTargetInScope(context, [...teams, ...teamIds])
}

export const isPersonInScope = (
  context: AuthorizationContext | null | undefined,
  target: PersonScopeTarget | null | undefined,
): boolean => isScopeTarget(target) && isTargetInScope(context, target.teamIds)

export const isTeamInScope = (
  context: AuthorizationContext | null | undefined,
  target: TeamScopeTarget | null | undefined,
): boolean =>
  isScopeTarget(target) &&
  typeof target.id === "string" &&
  target.id.trim().length > 0 &&
  isTargetInScope(context, [target.id])

export const isActivityInScope = (
  context: AuthorizationContext | null | undefined,
  target: ActivityScopeTarget | null | undefined,
): boolean =>
  isScopeTarget(target) && isTargetInScope(context, target.assignedTeams)

export const isHoursTargetInScope = (
  context: AuthorizationContext | null | undefined,
  target: HoursScopeTarget | null | undefined,
): boolean =>
  isScopeTarget(target) && isTargetInScope(context, target.assignedTeams)

export const areAllTeamsInScope = (
  context: AuthorizationContext | null | undefined,
  teamIds: unknown,
): boolean => {
  if (!context) return false
  const normalizedTeamIds = normalizeManagedTeamIds(teamIds)
  if (!normalizedTeamIds) return false
  if (context.scopePolicy === "tenant") return true

  const managedTeamIds = new Set(context.managedTeamIds)
  return normalizedTeamIds.every((teamId) => managedTeamIds.has(teamId))
}

export const canChangeTeamAssociations = (
  context: AuthorizationContext | null | undefined,
  sourceTeamIds: unknown,
  destinationTeamIds: unknown,
): boolean => {
  const normalizedSourceIds = normalizeManagedTeamIds(sourceTeamIds)
  const normalizedDestinationIds = normalizeManagedTeamIds(destinationTeamIds)
  if (!normalizedSourceIds || !normalizedDestinationIds) {
    return false
  }

  const sourceIds = new Set(normalizedSourceIds)
  const destinationIds = new Set(normalizedDestinationIds)
  const changedTeamIds = [
    ...normalizedSourceIds.filter((teamId) => !destinationIds.has(teamId)),
    ...normalizedDestinationIds.filter((teamId) => !sourceIds.has(teamId)),
  ]
  return (
    changedTeamIds.length > 0 && areAllTeamsInScope(context, changedTeamIds)
  )
}

export const MUTATION_FIELDS_BY_PERMISSION = {
  "users:update": ["firstName", "lastName", "phone"],
  "persons:update": ["firstName", "lastName", "birthDate", "email", "phone"],
} as const satisfies Partial<Record<Permission, readonly string[]>>

export const areMutationFieldsAllowed = (
  permission: Permission,
  changes: unknown,
): boolean => {
  if (!changes || typeof changes !== "object" || Array.isArray(changes)) {
    return false
  }
  const allowedFields =
    MUTATION_FIELDS_BY_PERMISSION[
      permission as keyof typeof MUTATION_FIELDS_BY_PERMISSION
    ]
  if (!allowedFields) return false

  const changedFields = Object.keys(changes)
  return (
    changedFields.length > 0 &&
    changedFields.every((field) =>
      (allowedFields as readonly string[]).includes(field),
    )
  )
}
